import React, { useRef, useState } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import { SOURCE_FILE_ACCEPT, SOURCE_FILE_LABEL, pendingFileMeta } from '../../../models/AssessmentsModel';
import '../Library/LibraryDropZone.css';

function fileIcon(entry) {
  if (entry.reject) return 'warning-circle';
  return entry.kind === 'transcript' ? 'file-text' : 'waveform';
}

/**
 * Add assessment's file slot (PRD-1703): any number of recordings or
 * transcripts, dropped or picked, one assessment each. Every file is listed
 * under the zone. A refused one says why and goes no further; one whose name
 * is already on an assessment warns; the rest show their transfer, which
 * starts at selection. Each can be taken out of the selection.
 *
 * `labelId` is the visible "Audio or transcript" label.
 */
function AssessmentFiles({ files, labelId, onFiles, onRemove }) {
  const pickerRef = useRef(null);
  const [dragDepth, setDragDepth] = useState(0);

  const take = (list) => {
    const picked = Array.from(list || []);
    if (picked.length) onFiles(picked);
  };

  // dragenter/dragleave fire for every child the pointer crosses, so count them.
  const dragHandlers = {
    onDragEnter: (e) => { e.preventDefault(); setDragDepth((d) => d + 1); },
    onDragOver: (e) => e.preventDefault(),
    onDragLeave: (e) => { e.preventDefault(); setDragDepth((d) => Math.max(0, d - 1)); },
    onDrop: (e) => {
      e.preventDefault();
      setDragDepth(0);
      take(e.dataTransfer?.files);
    },
  };

  return (
    <>
      <input
        ref={pickerRef}
        type="file"
        className="library-sr-only"
        accept={SOURCE_FILE_ACCEPT}
        multiple
        aria-labelledby={labelId}
        tabIndex={-1}
        onChange={(e) => { take(e.target.files); e.target.value = ''; }}
      />
      <div className={`library-drop${dragDepth > 0 ? ' is-over' : ''}`} {...dragHandlers}>
        <Icon name="waveform" size={28} className="library-drop-icon" />
        <div className="library-drop-hint">Drag and drop audio or an audio transcript file here</div>
        <div className="library-drop-sub">{SOURCE_FILE_LABEL} · one file per assessment</div>
        <button
          type="button"
          className="resui-btn resui-btn--tertiary resui-btn--small"
          aria-describedby={labelId}
          onClick={() => pickerRef.current?.click()}
        >
          Select from computer
        </button>
      </div>
      {files.length > 0 && (
        <ul className="assessment-files" aria-label="Selected files">
          {files.map((entry) => (
            <li key={entry.key} className={`assessment-file${entry.reject ? ' is-rejected' : entry.warn ? ' is-warn' : ''}`}>
              <Icon name={fileIcon(entry)} size={18} className="assessment-file-icon" />
              <div className="assessment-file-body">
                <div className="assessment-file-name">{entry.name}</div>
                <div className="assessment-file-meta">{pendingFileMeta(entry)}</div>
                {entry.reject && <div className="assessment-file-reason">{entry.reject}</div>}
                {entry.warn && <div className="assessment-file-reason">{entry.warn}</div>}
                {!entry.reject && (
                  <div className="assessment-file-status">
                    {entry.status === 'uploaded'
                      ? <Chip variant="success">Uploaded</Chip>
                      : <Chip>{`Uploading ${Math.round(entry.progress || 0)}%`}</Chip>}
                  </div>
                )}
              </div>
              <button type="button" className="library-icon-trigger" aria-label="Remove from selection" onClick={() => onRemove(entry.key)}>
                <Icon name="x" size={20} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default AssessmentFiles;
